import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import AppLayout from '@/components/layout/AppLayout.vue'
import '@/lib/breadcrumb'
import { detectBrowserLocale } from '@/lib/locale'

const localeRoutes: RouteRecordRaw[] = [
  {
    path: '',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
  },
  // Products
  {
    path: 'products/personalizer',
    name: 'personalizer',
    component: () => import('@/views/products/PersonalizerView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.products' }, { label: 'breadcrumb.personalizer' }],
    },
  },
  {
    path: 'products/personalizer/success-stories',
    name: 'personalizer-success-stories',
    component: () => import('@/views/products/PersonalizerSuccessStoriesView.vue'),
    meta: {
      breadcrumb: [
        { label: 'breadcrumb.products' },
        { label: 'breadcrumb.personalizer', name: 'personalizer' },
        { label: 'breadcrumb.successStories' },
      ],
    },
  },
  {
    path: 'products/maira',
    name: 'maira',
    component: () => import('@/views/products/MairaView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.products' }, { label: 'breadcrumb.maira' }],
    },
  },
  {
    path: 'products/maira/success-stories',
    name: 'maira-success-stories',
    component: () => import('@/views/products/MairaSuccessStoriesView.vue'),
    meta: {
      breadcrumb: [
        { label: 'breadcrumb.products' },
        { label: 'breadcrumb.maira', name: 'maira' },
        { label: 'breadcrumb.successStories' },
      ],
    },
  },
  {
    path: 'products/gcore',
    name: 'gcore',
    component: () => import('@/views/products/GcoreView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.products' }, { label: 'breadcrumb.gcore' }],
    },
  },
  {
    path: 'products/smartads',
    name: 'smartads',
    component: () => import('@/views/products/SmartAdsView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.products' }, { label: 'breadcrumb.smartads' }],
    },
  },
  {
    path: 'products/smartads/success-stories',
    name: 'smartads-success-stories',
    component: () => import('@/views/products/SmartAdsSuccessStoriesView.vue'),
    meta: {
      breadcrumb: [
        { label: 'breadcrumb.products' },
        { label: 'breadcrumb.smartads', name: 'smartads' },
        { label: 'breadcrumb.successStories' },
      ],
    },
  },
  // Company
  {
    path: 'company/about-us',
    name: 'about-us',
    component: () => import('@/views/company/AboutUsView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.company' }, { label: 'breadcrumb.aboutUs' }],
    },
  },
  {
    path: 'company/corporate-profile',
    name: 'corporate-profile',
    component: () => import('@/views/company/CorporateProfileView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.company' }, { label: 'breadcrumb.corporateProfile' }],
    },
  },
  {
    path: 'company/team',
    name: 'team',
    component: () => import('@/views/company/TeamView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.company' }, { label: 'breadcrumb.team' }],
    },
  },
  // Hidden team member detail page — printed on employee ID cards as
  // {base}/team/member/:uuid (no locale, no `company`). Locale-less hits are
  // redirected to /en by the global beforeEach guard below.
  {
    path: 'team/member/:employee_uuid',
    name: 'team-member',
    component: () => import('@/views/company/TeamMemberView.vue'),
    meta: {
      breadcrumb: [
        { label: 'breadcrumb.team', name: 'team' },
        { label: 'breadcrumb.teamMember' },
      ],
    },
  },
  {
    path: 'company/career',
    name: 'career',
    component: () => import('@/views/company/CareerView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.company' }, { label: 'breadcrumb.career' }],
    },
  },
  {
    path: 'company/life-at-gigalogy',
    name: 'life-at-gigalogy',
    component: () => import('@/views/company/LifeAtGigalogyView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.company' }, { label: 'breadcrumb.lifeAtGigalogy' }],
    },
  },
  // Resources — unified, tag-filtered Blog (/blog?tag=...)
  {
    path: 'blog/post/:slug',
    name: 'blog-post',
    component: () => import('@/views/resources/BlogPostView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.blog', name: 'blog' }, { label: 'breadcrumb.blogPost' }],
    },
  },
  {
    path: 'blog',
    name: 'blog',
    component: () => import('@/views/resources/BlogView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.blog' }],
    },
  },
  {
    path: 'blog/:tag',
    redirect: (to) => ({
      name: 'blog',
      params: { locale: to.params.locale },
      query: { tag: to.params.tag as string },
    }),
  },
  {
    path: 'resources/news',
    redirect: (to) => ({ name: 'blog', params: { locale: to.params.locale }, query: { tag: 'news' } }),
  },
  {
    path: 'resources/product-updates',
    redirect: (to) => ({ name: 'blog', params: { locale: to.params.locale }, query: { tag: 'product' } }),
  },
  {
    path: 'resources/case-studies',
    redirect: (to) => ({ name: 'blog', params: { locale: to.params.locale }, query: { tag: 'case-study' } }),
  },
  // Contact
  {
    path: 'contact',
    name: 'contact',
    component: () => import('@/views/ContactView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.contact' }],
    },
  },
  // Request a Demo
  {
    path: 'request-demo',
    name: 'request-demo',
    component: () => import('@/views/RequestDemoView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.requestDemo' }],
    },
  },
  // Developer / footer
  {
    path: 'developers',
    name: 'developers',
    component: () => import('@/views/DevelopersView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.developers' }],
    },
  },
  {
    path: 'sandbox',
    name: 'sandbox',
    component: () => import('@/views/SandboxView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.sandbox' }],
    },
  },
  {
    path: 'documentation',
    name: 'documentation',
    component: () => import('@/views/DocumentationView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.documentation' }],
    },
  },
  {
    path: 'tutorial',
    name: 'tutorial',
    component: () => import('@/views/TutorialView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.tutorial' }],
    },
  },
  // 404 catch-all for unmatched locale child routes
  {
    path: ':pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
  },
  // Legal
  {
    path: 'privacy-policy',
    name: 'privacy-policy',
    component: () => import('@/views/legal/PrivacyPolicyView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.privacyPolicy' }],
    },
  },
  {
    path: 'terms-and-conditions',
    name: 'terms-and-conditions',
    component: () => import('@/views/legal/TermsAndConditionsView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.termsConditions' }],
    },
  },
  {
    path: 'cookie-policy',
    name: 'cookie-policy',
    component: () => import('@/views/legal/CookiePolicyView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.cookiePolicy' }],
    },
  },
  {
    path: 'commercial-disclosure',
    name: 'commercial-disclosure',
    component: () => import('@/views/legal/CommercialDisclosureView.vue'),
    meta: {
      breadcrumb: [{ label: 'breadcrumb.commercialDisclosure' }],
    },
  },
]

const routes: RouteRecordRaw[] = [
  {
    path: '/:locale(en|ja)',
    component: AppLayout,
    children: localeRoutes,
  },
  {
    path: '/:pathMatch(.*)*',
    component: () => import('@/views/NotFoundView.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition ?? { top: 0 }
  },
})

router.beforeEach((to, _from, next) => {
  const locale = to.params.locale as string | undefined

  // Root path: redirect to the browser's preferred supported locale
  if (!locale) {
    const preferred = detectBrowserLocale()
    next({ path: `/${preferred}` + (to.path === '/' ? '' : to.path) })
    return
  }

  // Unknown locale segment: redirect to /en equivalent
  if (locale !== 'en' && locale !== 'ja') {
    next({ path: '/en' + to.path.replace(`/${locale}`, '') })
    return
  }

  document.documentElement.lang = locale

  next()
})

// Stale chunk hashes after a deploy invalidate old bundle URLs; reload once to pick up the new manifest.
const CHUNK_ERROR_PATTERN = /Failed to fetch dynamically imported module|Importing a module script failed/

router.onError((error, to) => {
  if (!CHUNK_ERROR_PATTERN.test(error.message)) return

  const reloadKey = 'chunk-reload-attempted'
  if (sessionStorage.getItem(reloadKey)) return

  sessionStorage.setItem(reloadKey, '1')
  window.location.href = to.fullPath
})

router.afterEach(() => {
  sessionStorage.removeItem('chunk-reload-attempted')
})

export default router
